// Ticket-lista (support, BELSŐ nézet) — a KÖZÖS DataTable-re épül (a clients.index / accounts / employees a
// referencia-minta): szerver-oldali (BFF) kereső (300 ms debounce) + státusz/típus-szűrő + darabszám-meta a
// toolbarban, szerver-rendezés (subject / accountName / createdAt / assigneeName), végtelen görgetés (100/lap).
// A /tickets BFF-végpontról fogyaszt (busz-sorok + fiók/ügyfél/assignee/requester/csapat-név feloldva; tenant-
// scope grant-fal RLS Odin-oldalon). Page-gate: `tickets.view`.
//
// Legacy-parity (régi CRM belső „Ticketek" lista, „Nyitott" tab layout 1:1): a MEGJELENÍTHETŐ oszlopok:
//   1. Jegy      — tárgy (itemTitle) + jegyszám + státusz-badge + típus-badge  (legacy „Ticket" + „Tartalom/tárgy")
//   2. Fiók      — fiók-név + tulajdonos ügyfél neve alatta                     (legacy „Fiók")
//   3. Igénylő   — a jegyet nyitó (openedBy → requester) avatar + név + létrehozás dátuma  (legacy „Igénylő")
//   4. Felelős   — a hozzárendelt kolléga avatar + név + support-csapat badge   (legacy „Felelős")
// A legacy „Tartalom" oszlop leírás-részlete NEM renderelhető (a description nincs a contractban → kihagyva).
// A `priority` HALOTT mező a legacy-ben (mindig `medium`, sehol nem jelenik meg) + nincs a contractban → nincs oszlop/szűrő.
// A legacy oszlop-szintű szabad-szöveg szűrők (fiók/igénylő/felelős) az egyesített keresőbe olvadnak (a BFF a
// tárgy/jegyszám/fiók/felelős/igénylő mezőkre illeszt). A sor kattintása a `/tickets/$id` részletnézetre navigál.
import { type TicketRowSchema, type TICKET_GROUP_STATUSES } from '@atlas/contracts';
import { TICKET_STATUSES, TICKET_TYPES } from '@atlas/shared';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  LockClosedIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type TFunction } from 'i18next';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type SVGProps,
} from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AppShell } from '@/components/shared/app-shell';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { DetailsExpander } from '@/components/shared/details-expander';
import { EmptyState } from '@/components/shared/empty-state';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListNewButton } from '@/components/shared/list-new-button';
import { RowActions, ViewRowAction } from '@/components/shared/list-row-actions';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { PersonChip } from '@/components/shared/person-chip';
import { TicketCreateSheet } from '@/components/tickets/ticket-create-sheet';
import { TicketStatusBadge } from '@/components/tickets/ticket-status-badge';
import { TicketTypeBadge } from '@/components/tickets/ticket-type-badge';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsCount, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Text } from '@/components/ui/text';
import { ticketKeys } from '@/features/tickets/api';
import { TICKET_LIST_ALL, TICKET_LIST_FILTER_DEFAULTS } from '@/features/tickets/list-filters';
import { useListFilters } from '@/hooks/use-list-filters';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { isForbiddenStatus } from '@/lib/error-status';
import { usePermissions } from '@/lib/use-permissions';

const ALL = TICKET_LIST_ALL;

// A SZŰRŐK search-paramok (a state-konvenció: a szűrt nézet legyen linkelhető és F5-biztos) — a séma a lap
// URL-szerződése, a `TICKET_LIST_FILTER_DEFAULTS` minden kulcsát ismeri. A default-értékű kulcs nem kerül az URL-be.
const ticketsSearchSchema = z.object({
  q: z.string().optional(),
  /**
   * Deep-link a LÉTREHOZÁSHOZ (`?new=1`) — a kezdőlap „Gyors műveletek" kártyája ide navigál (a create-akciók
   * onnan kerültek ki a kártya-fejlécekből; user-döntés).
   */
  new: z.string().optional(),
  status: z.string().optional(),
  type: z.string().optional(),
  group: z.string().optional(),
  mine: z.string().optional(),
  // SUPPORT-CSAPAT + LÉTREHOZÁS-DÁTUM tartomány. A szűrő bevezetésekor kimaradt innen; ettől még működött
  // (mérve: a router a nem ismert kulcsot az URL-ben hagyja), de a séma a lap URL-szerződése — a
  // `TICKET_LIST_FILTER_DEFAULTS` minden kulcsát teszt ellenőrzi ez ellen a séma ellen.
  team: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  closedFrom: z.string().optional(),
  closedTo: z.string().optional(),
  sort: z.string().optional(),
  order: z.string().optional(),
});

export const Route = createFileRoute('/tickets/')({
  validateSearch: (search) => ticketsSearchSchema.parse(search),
  beforeLoad: () => requireAuth(),
  component: TicketsPage,
});

type TicketRow = z.infer<typeof TicketRowSchema>;

const PAGE_SIZE = 100;
/** A „Hozzám rendelt" szűrő BE értéke (a szűrő egy két-állapotú select: mind / hozzám rendelt). */
const MINE_ON = 'mine';

// A legacy enum-kulcsok (adat-parity a régi CRM-mel) — a címkék az i18n-ből.
const STATUS_OPTIONS = TICKET_STATUSES;
/** Az életciklus-fülek kulcsai (a contract csoport-térképéből — egy igazságforrás). */
type TicketGroup = keyof typeof TICKET_GROUP_STATUSES;
/**
 * ÉLETCIKLUS-FÜLEK — KETTŐ: aktív („Folyamatban") és „Lezárt".
 *
 * Előbb a „Mind" fül ment, most a „Nyitott" is (user-döntés): a fül csak egy plusz
 * döntést kért a szkennelés előtt, miközben a sor STÁTUSZ-JELVÉNYE kiírja, hogy a jegy nyitott, folyamatban
 * vagy újranyitott, a szűkítést pedig a szűrő-panel státusz-szűrője adja. Az „aktív" halmaz így teljes —
 * `open` + `in_progress` + `reopened` (`TICKET_GROUP_STATUSES`) —, tehát egyetlen jegy sem esik ki a listáról,
 * és a fülezés MEGEGYEZIK a partner-portáléval. Minden fül IKONT is kap — a szöveg mellett az ikon gyorsítja
 * a felismerést (és a mobil-fülön a szöveg csordulhat).
 */
const GROUP_TABS: TicketGroup[] = ['in_progress', 'closed'];
const GROUP_ICONS: Record<TicketGroup, ComponentType<SVGProps<SVGSVGElement>>> = {
  in_progress: ArrowPathIcon,
  closed: CheckCircleIcon,
};
const TYPE_OPTIONS = TICKET_TYPES;

/**
 * A jegy „Részletek" sorai: JEGYSZÁM · LEADVA · SUPPORT-CSAPAT · KOMMENTEK. Tiszta függvény (a `t` és a
 * `locale` injektálva) — ugyanezt a listát a DESKTOP kibontó és a MOBIL-kártya `cardExtraItems`-e is használja
 * (egy forrás, kétféle kontextus).
 *
 * MIÉRT ITT a jegyszám: másodlagos azonosító (a keresőben és a részletekben kell, a szkennelést viszont a
 * TÁRGY vezeti) — user-döntés.
 * MIÉRT ITT a LEADÁS DÁTUMA és a SUPPORT-CSAPAT (user-döntés): eddig az Igénylő, illetve a Felelős
 * oszlop MÁSODIK sorában álltak. Egy személy-oszlopban a második sor a szkennelést zavarja (a szem a nevet
 * keresi), az információ pedig másodlagos — a részletek helye. A rendezés NEM változik: a fejléc-rendezés
 * továbbra is `createdAt` (Igénylő) és `assigneeName` (Felelős) szerint megy.
 */
function ticketDetailRows(
  tk: TicketRow,
  t: TFunction,
  locale: string,
): { label: string; value: string }[] {
  const rows: { label: string; value: string }[] = [];
  if (tk.ticketNumber) rows.push({ label: t('tickets.detailsNumber'), value: tk.ticketNumber });
  const created = fmtDate(tk.createdAt, locale);
  if (created) rows.push({ label: t('tickets.detailsCreated'), value: created });
  const closed = fmtDate(tk.closedAt, locale);
  if (closed) rows.push({ label: t('tickets.detailsClosed'), value: closed });
  if (tk.supportTeamName) {
    rows.push({ label: t('tickets.detailsSupportTeam'), value: tk.supportTeamName });
  }
  rows.push({ label: t('tickets.detailsComments'), value: String(tk.commentCount) });
  if (tk.internalNoteCount > 0) {
    rows.push({ label: t('tickets.detailsInternalNotes'), value: String(tk.internalNoteCount) });
  }
  return rows;
}

/**
 * BELSŐ JEGYZET jelzés a sorban (user-lelet: a listán nem látszott, csak a jegyen belül a fül számlálója). A
 * belső jegyzet fül-ikonja (lakat) + darabszám; nincs belső jegyzet → nincs jelvény (nem zaj). Darabszám-jelvény,
 * ezért a nyers `Badge` atom (a `DomainBadge` az enum-értékeké).
 */
function InternalNoteBadge({ count, t }: { count: number; t: TFunction }): JSX.Element | null {
  if (count <= 0) return null;
  const label = t('tickets.internalNotesBadge', { count });
  return (
    <Badge
      variant="muted"
      icon={<LockClosedIcon className="size-3" />}
      title={label}
      aria-label={label}
    >
      {count}
    </Badge>
  );
}

/** ISO → lokalizált rövid dátum (a UI nyelvének megfelelő formátumban). */
function fmtDate(iso: string | null, locale: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(locale);
}

export function TicketsPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { can, isLoading: permsLoading } = usePermissions();
  const canView = can('tickets.view');
  // Jog-vezérelt sor-akció: tickets.manage → ceruza (státusz-kezelő szerkesztő-sheet). Ticket-törlés a legacyben
  // sincs (lezárás van) → nincs kuka. A create (typeahead + #TKT-szám) + komment/értesítés külön increment.
  const canManage = can('tickets.manage');

  const [createOpen, setCreateOpen] = useState(false);
  // A SZŰRŐK az URL-ben (linkelhető + F5-biztos nézet); a defaultok egyben a kulcs-készlet.
  const { filters: urlFilters, setFilters } = useListFilters(TICKET_LIST_FILTER_DEFAULTS);
  // Deep-link: `?new=1` → EGYSZER nyitjuk a create-sheetet, és a paramot azonnal töröljük (replace), hogy a
  // visszalépés/újratöltés ne nyissa újra. Jog nélkül no-op (a mentés amúgy is 403-ba futna).
  const newSheetOpened = useRef(false);
  useEffect(() => {
    if (urlFilters.new !== '1' || newSheetOpened.current || !canManage) return;
    newSheetOpened.current = true;
    setCreateOpen(true);
    setFilters({ new: '' });
  }, [urlFilters.new, canManage, setFilters]);

  const q = urlFilters.q;
  const status = urlFilters.status;
  const type = urlFilters.type;
  const team = urlFilters.team;
  const from = urlFilters.from;
  const to = urlFilters.to;
  const group = urlFilters.group as TicketGroup;
  // A LEZÁRÁS-tartomány csak a „Lezárt" fülön hat (a fülváltás amúgy is üríti, lásd a `Tabs`-nál).
  const closedFrom = group === 'closed' ? urlFilters.closedFrom : '';
  const closedTo = group === 'closed' ? urlFilters.closedTo : '';
  const sort = urlFilters.sort;
  const order = urlFilters.order as 'asc' | 'desc';
  // A beviteli mező AZONNALI (lokális) állapota — az URL-be a debounce-olt érték kerül.
  const [searchInput, setSearchInput] = useState(q);
  /**
   * ÉLETCIKLUS-FÜL — KETTŐ: `in_progress` („Folyamatban": open+in_progress+reopened) · `closed`
   * (closed+resolved). A legacy három tabja (`opened` külön) és a korábbi „Mind" fül is kivezetve
   * (user-döntés, l. a `GROUP_TABS` indoklását). Alap: a „Folyamatban" fül.
   */
  /**
   * „Csak az enyém" — a legacy a saját NEVÉRE előszűrte a listát, ha a felhasználónak volt jegye az adott
   * fülön. Ezt tartjuk: az első válasz `mineGroups`-ából kapcsoljuk BE (egyszer, lásd lent), utána a
   * felhasználó dönt.
   */
  // A „csak az enyém" is URL-állapot, de BOOLEAN: az URL-ben `mine=1` (a hiányzó param a kikapcsolt állapot).
  const mine = urlFilters.mine === '1';
  const setMine = useCallback(
    (next: boolean) => setFilters({ mine: next ? '1' : '' }),
    [setFilters],
  );
  const mineDecided = useRef(false);
  // Szerver-rendezés: a `sort` a kulcs, az `order` az irány. Alap: createdAt DESC (a legacy default).

  // Kereső-debounce (300 ms).
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === q) return;
    const timer = setTimeout(() => setFilters({ q: trimmed }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, q, setFilters]);

  // Page-gate: tickets.view nélkül meg sem kérdezzük a szervert (a BFF is 403-at adna) → azonnali jog-hiba.
  const gated = !permsLoading && !canView;

  // SUPPORT-CSAPAT szűrő opciói a MEGLÉVŐ `ticketRefs` végpontból (a szerkesztő-form is ezt használja —
  // nincs új endpoint és nincs második igazság-forrás). Jog-kapu: tickets.view, ugyanaz, mint a listán.
  const refsQ = api.ticketRefs.useQuery(['ticket-refs'], {}, { queryKey: ['ticket-refs'] });
  const teamOptions = useMemo(
    () =>
      refsQ.data?.status === 200
        ? [...refsQ.data.body.supportTeams]
            .map((tm) => ({ value: tm.id, label: tm.name }))
            .sort((a, b) => a.label.localeCompare(b.label, i18n.language))
        : [],
    [refsQ.data, i18n.language],
  );

  const filters = {
    q,
    status,
    type,
    team,
    from,
    to,
    closedFrom,
    closedTo,
    sort,
    order,
    group,
    mine,
  };
  const listKey = ticketKeys.list(filters);

  const infinite = useInfiniteQuery({
    queryKey: listKey,
    enabled: !gated,
    queryFn: ({ pageParam }) =>
      api.tickets.query({
        query: {
          q: q || undefined,
          group,
          mine: mine ? true : undefined,
          status: status === ALL ? undefined : status,
          type: type === ALL ? undefined : type,
          supportTeamId: team === ALL ? undefined : team,
          createdFrom: from || undefined,
          createdTo: to || undefined,
          closedFrom: closedFrom || undefined,
          closedTo: closedTo || undefined,
          sort,
          order,
          limit: PAGE_SIZE,
          offset: pageParam,
        },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.status === 200 ? (lastPage.body.nextOffset ?? undefined) : undefined,
  });

  const pages = useMemo(() => infinite.data?.pages ?? [], [infinite.data]);
  const rows = useMemo(
    () => pages.flatMap((p) => (p.status === 200 ? p.body.tickets : [])),
    [pages],
  );
  const okPage = pages.find((p) => p.status === 200);
  const total = okPage?.status === 200 ? okPage.body.total : 0;
  // A BFF jelzi, ha a lista a védő-plafon miatt CSONKA (a darabszám és minden aggregátum is az) —
  // ezt kiírjuk a meta-sorba: egy csendben elvágott lista hibás döntést alapoz meg.
  const truncated = okPage?.status === 200 ? okPage.body.truncated : false;
  const hasNoSuccess = pages.length > 0 && !pages.some((p) => p.status === 200);
  const isError = gated || infinite.isError || hasNoSuccess;
  const isForbidden = gated || pages.some((p) => isForbiddenStatus(p.status));
  const hasFilters =
    q !== '' || status !== ALL || type !== ALL || team !== ALL || from !== '' || to !== '' || mine;
  const groupCounts = okPage?.status === 200 ? okPage.body.groups : undefined;
  const mineCounts = okPage?.status === 200 ? okPage.body.mineGroups : undefined;

  // A legacy alap-szűrője: ha a belépett usernek VAN jegye az induló fülön, a „csak az enyém" bekapcsolva
  // indul. EGYSZER dönt (az első sikeres válasznál) — utána a felhasználó választása marad érvényben.
  useEffect(() => {
    if (mineDecided.current || mineCounts === undefined) return;
    mineDecided.current = true;
    const own = mineCounts[group];
    if (own > 0) setMine(true);
  }, [mineCounts, group]);

  // Rendezés: azonos oszlopra kattintás → irányváltás; új oszlopra → az adott kulcs ASC.
  const toggleSort = useCallback(
    (col: string) => {
      setFilters(
        col === sort ? { order: order === 'asc' ? 'desc' : 'asc' } : { sort: col, order: 'asc' },
      );
    },
    [sort, order, setFilters],
  );
  const { fetchNextPage } = infinite;
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  // Halvány em-dash az üres tartalmi cellákhoz.
  const emDash = <span className="text-muted-foreground">—</span>;
  const locale = i18n.language;

  const columns: DataTableColumn<TicketRow>[] = [
    {
      // 1. Jegy — tárgy (rendezhető, itemTitle) + jegyszám + típus-badge + komment-szám. A státusz KÜLÖN, dedikált
      //    utolsó oszlop (a lista-konvenció: státusz mindig az utolsó oszlop).
      id: 'subject',
      header: t('tickets.columns.ticket'),
      sortable: true,
      cardLabel: t('tickets.columns.ticket'),
      width: 'minmax(0,2.5fr)',
      multiline: true,
      // MOBIL-kártya: a részlet-párok a kártya EGYETLEN „Részletek" rácsába.
      cardExtraItems: (tk) =>
        ticketDetailRows(tk, t, locale).map((r) => ({ label: r.label, value: r.value })),
      // MOBIL-kártya FEJLÉCE: tárgy + típus-jelvény — a desktop-cella `DetailsExpander`-e NÉLKÜL. Ez utóbbi
      // volt a „KÉT Részletek egy kártyán" lelet (user-lelet): a kártyának MÁR van egy kibontója,
      // ami alá a `cardExtraItems` be is teszi ugyanezeket a párokat, tehát a cella saját kibontója egy
      // második, félreérthető kapcsoló lett volna. A minta az eszköz- és a partner-jegy-lista `cardCell`-je.
      cardCell: (tk) => (
        <span className="flex min-w-0 flex-col items-start gap-1.5">
          <span className="min-w-0 truncate">{tk.subject || emDash}</span>
          <span className="flex flex-wrap items-center gap-1.5">
            {tk.type !== '' && (
              <TicketTypeBadge
                type={tk.type}
                label={t(`tickets.typeLabel.${tk.type}`, { defaultValue: tk.type })}
              />
            )}
            <InternalNoteBadge count={tk.internalNoteCount} t={t} />
          </span>
        </span>
      ),
      cell: (tk) => (
        <div className="min-w-0 space-y-1.5">
          <Text variant="itemTitle" as="div" className="truncate">
            {tk.subject || emDash}
          </Text>
          {/* LEÍRÁS-KIVONAT a cím alatt, halványabban és könnyebben — a PARTNER-lista mintája (user-döntés,
              2026-09-08): a tárgy önmagában gyakran nem mondja meg, miről van szó. SIMA SZÖVEG: a kivonatot a
              BFF strippeli (`plainTextExcerpt`), tehát a cellába nem juthat nyers HTML. */}
          {tk.descriptionExcerpt !== '' && (
            <Text tone="tertiary" as="div" className="line-clamp-1">
              {tk.descriptionExcerpt}
            </Text>
          )}
          {/* „> Részletek" (jegyszám + komment-szám) + MELLETTE a típus-badge (ikonnal) — user-döntés: a
              jegyszám másodlagos azonosító, a részletek helye; a típus viszont szkennelendő, marad kívül. */}
          <DetailsExpander
            rows={ticketDetailRows(tk, t, locale)}
            trailing={
              <>
                {tk.type && (
                  <TicketTypeBadge
                    type={tk.type}
                    label={t(`tickets.typeLabel.${tk.type}`, { defaultValue: tk.type })}
                  />
                )}
                <InternalNoteBadge count={tk.internalNoteCount} t={t} />
              </>
            }
          />
        </div>
      ),
    },
    {
      // 2. Fiók — fiók-név (rendezhető) + a tulajdonos ügyfél neve alatta (a legacy „Fiók" oszlop 1:1).
      id: 'accountName',
      card: 'subtitle',
      header: t('tickets.columns.account'),
      sortable: true,
      cardLabel: t('tickets.columns.account'),
      width: 'minmax(0,1.5fr)',
      multiline: true,
      cell: (tk) =>
        tk.accountName || tk.clientName ? (
          <div className="min-w-0 space-y-0.5">
            {/* A fiók-név tipográfiája a szervezeti-felépítés „Beosztások" oszlopával EGYEZŐ (sima, a
                sor-tipográfiát öröklő span — user-döntés), nem a nagyobb `body` variáns. */}
            {tk.accountName && <span className="block min-w-0 truncate">{tk.accountName}</span>}
            {/* Az ÜGYFÉL-név a másodlagos sor: ugyanaz a tipográfia, mint a számlázási profilok „Cím"
                oszlopában az ország (`tone="tertiary"`) — user-döntés. */}
            {tk.clientName && (
              <Text tone="tertiary" as="div" className="truncate">
                {tk.clientName}
              </Text>
            )}
          </div>
        ) : (
          emDash
        ),
    },
    {
      // 3. Igénylő — a jegyet nyitó (requester) avatarja + neve (rendezhető: createdAt). A leadás dátuma a
      // „Részletek" alatt (user-döntés).
      // Mobil-kártyán REJTVE (`hideOnCard`) — a kártyán a tárgy + fiók + FELELŐS + státusz a lényeg (az igénylő
      // másodlagos a szkenneléshez; best practice: cím + kulcs-metaadatok + státusz, nem minden oszlop).
      id: 'createdAt',
      header: t('tickets.columns.requester'),
      sortable: true,
      cardLabel: t('tickets.columns.requester'),
      width: 'minmax(0,1.5fr)',
      multiline: true,
      card: 'hidden',
      // A LEADÁS DÁTUMA a „Részletek" alá került (user-döntés) — itt CSAK a személy áll. A fejléc-rendezés
      // kulcsa VÁLTOZATLANUL a `createdAt` (az oszlop id-je), tehát az „Igénylő" fejlécre kattintva továbbra is
      // a leadás dátuma szerint rendez.
      cell: (tk) =>
        tk.requesterName ? (
          <PersonChip
            name={tk.requesterName}
            givenName={tk.requesterGivenName}
            familyName={tk.requesterFamilyName}
            avatarUrl={tk.requesterAvatarUrl}
            size="sm"
            className="min-w-0 gap-1.5"
          />
        ) : (
          // VAN igénylő-id, csak a neve nem oldható fel → TÖRÖLT személy (nem „ismeretlen": az adat megvan,
          // a személy offboardolt). Id nélkül marad az „Ismeretlen igénylő" (adathiba-jelzés).
          <span className="text-muted-foreground">
            {tk.requesterId ? t('common.deletedUser') : t('tickets.unknownRequester')}
          </span>
        ),
    },
    {
      // 4. Felelős — a hozzárendelt kolléga avatarja + neve (rendezhető: assigneeName). A support-csapat a
      // „Részletek" alatt (user-döntés).
      id: 'assigneeName',
      card: 'subtitle',
      header: t('tickets.columns.assignee'),
      sortable: true,
      cardLabel: t('tickets.columns.assignee'),
      width: 'minmax(0,1.5fr)',
      multiline: true,
      // A SUPPORT-CSAPAT a „Részletek" alá került (user-döntés) — itt CSAK a személy áll.
      cell: (tk) =>
        tk.assigneeName ? (
          <PersonChip
            name={tk.assigneeName}
            givenName={tk.assigneeGivenName}
            familyName={tk.assigneeFamilyName}
            avatarUrl={tk.assigneeAvatarUrl}
            accentColor={tk.assigneeTeamColor}
            size="sm"
            className="min-w-0 gap-1.5"
          />
        ) : tk.assigneeId ? (
          // VAN felelős-id, csak nem oldható fel → TÖRÖLT személy. A „—" itt HAZUDNA (kiosztatlannak
          // mutatna egy triázsolt jegyet) — mérve.
          <span className="text-muted-foreground">{t('common.deletedUser')}</span>
        ) : (
          // FELELŐS NÉLKÜL „—" (nem szöveg): a partner-listával egyező, és a „Nincs felelős" mondat a
          // szkennelésnél zajt adott — egy üres cella jelölése a gondolatjel (user-döntés).
          emDash
        ),
    },
    {
      // 5. Státusz — a jegy állapota (utolsó, dedikált oszlop; a lista-konvenció szerint a státusz mindig utolsó).
      id: 'status',
      header: t('tickets.columns.status'),
      cardLabel: t('tickets.columns.status'),
      width: 'minmax(0,1fr)',
      cell: (tk) => (
        <TicketStatusBadge
          status={tk.status}
          label={t(`tickets.statusLabel.${tk.status}`, { defaultValue: tk.status })}
        />
      ),
    },
  ];

  return (
    <AppShell
      // Mobilon a lebegő FAB kezeli a létrehozást (csak manage-jog mellett); desktopon a toolbar-gomb (lásd lent).
      primaryAction={
        canManage
          ? {
              icon: <PlusIcon className="size-5 stroke-2" />,
              label: t('tickets.new'),
              onClick: () => setCreateOpen(true),
            }
          : undefined
      }
      toolbar={
        // ÉLETCIKLUS-FÜLEK a kereső FÖLÖTT (a legacy három tabjának utódja: KETTŐ fül,
        // darabszám-badge-ekkel — az assets/
        // jogosultságok-oldal bevett tab-mintája). Alatta a KÖZÖS ListToolbar: kereső + „Szűrők (N)" popover
        // (státusz → típus) + darabszám-meta. A szűrők a gomb mögé csukódnak → a sáv sosem tördel/csordul.
        <div className="flex flex-col">
          <div className="flex flex-wrap items-center gap-3 px-4 pt-2">
            <Tabs
              value={group}
              // a lezárás-tartomány a „Lezárt" fül saját szűrője — máshová váltva nem marad rejtett, aktív szűrő
              onValueChange={(v) =>
                setFilters(
                  v === 'closed' ? { group: v } : { group: v, closedFrom: '', closedTo: '' },
                )
              }
            >
              <TabsList>
                {GROUP_TABS.map((g) => {
                  const count = groupCounts === undefined ? null : groupCounts[g];
                  const Icon = GROUP_ICONS[g];
                  return (
                    <TabsTrigger key={g} value={g}>
                      <Icon />
                      {t(`tickets.tab.${g}`)}
                      {/* A badge CSAK ott, ahol van mit mutatni (a legacy is csak nem-nulla számot írt ki). */}
                      {count !== null && count > 0 && <TabsCount>{count}</TabsCount>}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </Tabs>
          </div>
          <ListToolbar
            search={{
              value: searchInput,
              onChange: setSearchInput,
              placeholder: t('common.searchPlaceholder'),
            }}
            // A SORREND user-döntés: „Hozzám rendelt" ELÖL (ez a napi belépési pont), utána a TÍPUS és
            // közvetlenül mellette a SUPPORT-CSAPAT (a két „mi ez / kihez tartozik" szűrő egymás
            // mellé), majd a létrehozás-dátum tartomány, és a STÁTUSZ az UTOLSÓ. A `filterItems` azért kell,
            // mert a fajta szerinti rendezés (select-ek → dátumok) a dátumokat a végére sodorná.
            filterItems={[
              {
                kind: 'select',
                key: 'mine',
                label: t('tickets.filterMine'),
                value: mine ? MINE_ON : ALL,
                onChange: (v: string) => setMine(v === MINE_ON),
                options: [{ value: MINE_ON, label: t('tickets.filterMineOn') }],
                allValue: ALL,
              },
              {
                kind: 'select',
                key: 'type',
                label: t('tickets.filterType'),
                value: type,
                onChange: (v: string) => setFilters({ type: v }),
                options: TYPE_OPTIONS.map((o) => ({
                  value: o,
                  label: t(`tickets.typeLabel.${o}`, { defaultValue: o }),
                })),
                allValue: ALL,
              },
              {
                kind: 'select',
                key: 'team',
                label: t('tickets.filterTeam'),
                value: team,
                onChange: (v: string) => setFilters({ team: v }),
                options: teamOptions,
                allValue: ALL,
                // CSAPAT-nevek (pl. „RS Booking") → nem kisbetűsítjük.
                preserveCase: true,
              },
              {
                kind: 'date',
                key: 'from',
                label: t('tickets.filterFrom'),
                value: from,
                onChange: (v: string) => setFilters({ from: v }),
              },
              {
                kind: 'date',
                key: 'to',
                label: t('tickets.filterTo'),
                value: to,
                onChange: (v: string) => setFilters({ to: v }),
              },
              // A „Lezárt" fülön a LEZÁRÁS ideje szerinti tartomány is (Évi #78: a létrehozás napja itt félrevezető).
              ...(group === 'closed'
                ? [
                    {
                      kind: 'date' as const,
                      key: 'closedFrom',
                      label: t('tickets.filterClosedFrom'),
                      value: closedFrom,
                      onChange: (v: string) => setFilters({ closedFrom: v }),
                    },
                    {
                      kind: 'date' as const,
                      key: 'closedTo',
                      label: t('tickets.filterClosedTo'),
                      value: closedTo,
                      onChange: (v: string) => setFilters({ closedTo: v }),
                    },
                  ]
                : []),
              {
                kind: 'select',
                key: 'status',
                label: t('tickets.filterStatus'),
                value: status,
                onChange: (v: string) => setFilters({ status: v }),
                options: STATUS_OPTIONS.map((s) => ({
                  value: s,
                  label: t(`tickets.statusLabel.${s}`, { defaultValue: s }),
                })),
                allValue: ALL,
              },
            ]}
            meta={
              infinite.isFetching && !infinite.isFetchingNextPage
                ? t('tickets.loading')
                : !isError && !infinite.isLoading
                  ? t('tickets.countOf', { shown: rows.length, total }) +
                    (truncated ? ` · ${t('common.listTruncated')}` : '')
                  : ''
            }
            actions={
              // A doboz MOBILON `display: contents` (mért hiba, 375 px): flexként üresen is elvitte a
              // sáv térközét, tehát a kereső + „Szűrők" sor a jobb szél ELŐTT ért véget.
              <div className="contents md:flex md:items-center md:gap-2">
                {/* A New gomb CSAK ≥md — mobilon a lebegő FAB (primaryAction) kezeli a létrehozást. */}
                <div className="hidden md:block">
                  <ListNewButton
                    label={t('tickets.new')}
                    onClick={() => setCreateOpen(true)}
                    enabled={canManage}
                    disabledTooltip={t('common.noCreatePermission')}
                  />
                </div>
              </div>
            }
          />
        </div>
      }
    >
      <div className="flex h-full flex-col px-4 pb-2">
        <DataTable<TicketRow>
          columns={columns}
          rows={rows}
          getRowId={(tk) => tk.id}
          onRowClick={(tk) => void navigate({ to: '/tickets/$id', params: { id: tk.id } })}
          sort={sort}
          order={order}
          onSort={toggleSort}
          isLoading={!gated && (permsLoading || infinite.isLoading)}
          isError={isError}
          errorState={
            <ListErrorState forbidden={isForbidden} onRetry={() => void infinite.refetch()} />
          }
          emptyState={
            hasFilters ? (
              <EmptyState title={t('tickets.noMatches')} description={t('tickets.noMatchesDesc')} />
            ) : (
              <EmptyState title={t('tickets.empty')} description={t('tickets.emptyDesc')} />
            )
          }
          hasNextPage={infinite.hasNextPage}
          isFetchingNextPage={infinite.isFetchingNextPage}
          onLoadMore={loadMore}
          rowActionsLabel={t('tickets.columns.actions')}
          actionsColWidth="4rem"
          // MOBILON NINCS ⋯ menü: a sor egyetlen akciója a jegy MEGNYITÁSA, amit a kártya koppintása már
          // elvégez — a menü ugyanazt kínálta egy plusz lépéssel (user-döntés).
          hideCardActions
          // SOR-AKCIÓ: MEGNYITÁS (szemecske) a részletes felületre — NEM szerkesztő-sheet (user-döntés).
          // MIÉRT: a jegy-módosítás (státusz, felelős, csapat, komment) a részletes oldalon
          // teljes körű; egy külön szerkesztő-sheet ugyanazt a néhány mezőt duplikálta, két helyen tartva a
          // formot és a jog-kapukat. A gomb MINDENKINEK látszik (nem `canManage`-elt): a megnyitás olvasás.
          rowActions={(tk) => (
            <RowActions>
              <ViewRowAction
                label={t('tickets.open')}
                onClick={() => void navigate({ to: '/tickets/$id', params: { id: tk.id } })}
              />
            </RowActions>
          )}
        />
      </div>

      <TicketCreateSheet open={createOpen} onOpenChange={setCreateOpen} />
    </AppShell>
  );
}
